import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Pill, EmptyState } from "./AdminUI";

const COLORS = ["#2E6B57", "#F59E0B", "#3B82F6", "#94A3B8", "#EF4444"];
const STATUSES = ["new", "in_progress", "waiting_on_user", "resolved", "closed"];

export default function SupportTab() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/support/tickets", { params: status ? { status } : {} }).then(r => setData(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const openTicket = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    const r = await api.get(`/admin/support/tickets/${id}`);
    setDetail(r.data);
  };
  const setTicketStatus = async (id, s) => {
    try { await api.patch(`/admin/support/tickets/${id}/status`, { status: s }); toast.success("Updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addNote = async (id) => {
    if (!note.trim()) return;
    try { await api.post(`/admin/support/tickets/${id}/note`, { text: note }); toast.success("Note added"); setNote(""); const r = await api.get(`/admin/support/tickets/${id}`); setDetail(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p className="text-sm text-[#222]/45">Loading…</p>;

  return (
    <div data-testid="admin-support-tab">
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5 mb-6 max-w-sm">
        <h3 className="text-sm font-bold mb-3">Submissions by status</h3>
        {data.status_distribution.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">No submissions yet</p> : (
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data.status_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} label>
                {data.status_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <select value={status} onChange={e => setStatus(e.target.value)} className="text-xs px-3 py-2 border border-black/10 rounded-full" data-testid="admin-support-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {data.tickets.length === 0 ? (
        <EmptyState title="No submissions" body="Contact-form submissions will show up here." testid="admin-support-empty" />
      ) : (
        <div className="space-y-3">
          {data.tickets.map(t => (
            <div key={t.id} className="bg-white rounded-2xl p-4 shadow-sm border border-black/5" data-testid={`admin-ticket-${t.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 cursor-pointer" onClick={() => openTicket(t.id)}>
                <div>
                  <p className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                    {t.name}
                    {t.topic && <Pill tone="neutral">{t.topic}</Pill>}
                    <Pill tone={t.status === "resolved" ? "green" : "neutral"}>{t.status}</Pill>
                    {t.type === "contact" && t.email_delivered === false && <Pill tone="amber">not emailed</Pill>}
                  </p>
                  <p className="text-xs text-[#222]/50">{t.email} · {new Date(t.created_at).toLocaleString()}{t.role ? ` · ${t.role}` : ""}</p>
                  <p className="text-xs text-[#222]/70 mt-1">{t.message.slice(0, 140)}{t.message.length > 140 ? "…" : ""}</p>
                </div>
                <select value={t.status} onChange={e => setTicketStatus(t.id, e.target.value)} onClick={e => e.stopPropagation()} className="text-xs px-2 py-1 border border-black/10 rounded-full" data-testid={`admin-ticket-status-${t.id}`}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {expanded === t.id && detail && (
                <div className="mt-3 border-t border-black/5 pt-3 text-xs">
                  {detail.account && (
                    <p className="text-[#222]/60 mb-2">Account: {detail.account.role} · joined {new Date(detail.account.created_at).toLocaleDateString()}
                      {detail.account.suspended && <span className="text-amber-700"> · Suspended</span>}
                    </p>
                  )}
                  {detail.verification && (
                    <p className="text-[#222]/60 mb-2">Verification: {detail.verification.status} — {detail.verification.completed}/{detail.verification.total} complete
                      {detail.verification.missing_labels?.length > 0 && `. Missing: ${detail.verification.missing_labels.join(", ")}`}
                    </p>
                  )}
                  <div className="space-y-1 mb-2">
                    {(detail.ticket.notes || []).map((n, i) => <p key={i} className="text-[#222]/60">{n.by}: {n.text}</p>)}
                  </div>
                  <div className="flex gap-2">
                    <input value={note} onChange={e => setNote(e.target.value)} placeholder="Internal note…" className="flex-1 px-3 py-1.5 border border-black/10 rounded-full outline-none" data-testid={`admin-ticket-note-input-${t.id}`} />
                    <button onClick={() => addNote(t.id)} className="px-3 py-1.5 rounded-full bg-black/5 font-semibold">Add</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
